import React, { useEffect, useId } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Skeleton,
} from '@mui/material';
import EastIcon from '@mui/icons-material/East';
import SearchInput from '../../../common/SearchInputField';
import NoDataFound from "../../../dashboardcomponents/Traffic/NoDataFound";
import { useSelector } from 'react-redux';
import { loadGoogleFont, useScopedFontStyle } from "../../../../utils";

// Scoped font hook — specificity (0,2,0) beats global index.scss (0,1,0)


const ArticleListKnowledgeBase = ({
  subCategories = [],
  articles = [],
  onSubCategoryClick,
  onArticleClick,
  initialSearch = '',
  minHeight = '620px',
  onKeyDown,
  searchQuery: controlledSearchQuery,
  setSearchQuery: controlledSetSearchQuery,
  loading = false,
}) => {
  const { data: kbSettings, initialized } = useSelector((state) => state.externalKbSettings);
  const fontFamily = kbSettings?.data?.font_family || 'Inter';

  const rawId = useId();
  const scopeId = rawId.replace(/:/g, '');

  useEffect(() => {
    loadGoogleFont(fontFamily);
  }, [fontFamily]);

  useScopedFontStyle(scopeId, fontFamily);

  const hasCategories = subCategories.length > 0;

  const [internalSearchQuery, setInternalSearchQuery] = React.useState(initialSearch);

  const searchQuery = controlledSearchQuery !== undefined
    ? controlledSearchQuery
    : internalSearchQuery;

  const setSearchQuery = controlledSetSearchQuery !== undefined
    ? controlledSetSearchQuery
    : setInternalSearchQuery;

  const [selectedCategory, setSelectedCategory] = React.useState('');

  React.useEffect(() => {
    if (
      hasCategories &&
      !subCategories?.some(cat => cat.name === selectedCategory) &&
      subCategories[0]?.name
    ) {
      const firstCat = subCategories[0];
      setSelectedCategory(firstCat.name);
      if (onSubCategoryClick && firstCat.slug) {
        onSubCategoryClick(firstCat.slug);
      }
    }
  }, [subCategories, hasCategories, onSubCategoryClick, selectedCategory]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const filteredArticles = articles?.filter((article) =>
    article?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase() || '')
  );

  const renderArticlesContent = () => {
    if (loading || !initialized) {
      return Array.from(new Array(3)).map((_, index) => (
        <Box
          key={index}
          sx={{
            mb: '10px',
            maxWidth: '840px',
            height: '60px',
            paddingLeft: '46px',
            py: '5px',
            pr: '10px',
            border: '1px solid #D5DCE1',
            borderRadius: '4px',
          }}
        >
          <Skeleton variant="text" height={20} width="65%" sx={{ mb: '8px' }} />
          <Skeleton variant="text" height={20} width="45%" />
        </Box>
      ));
    }

    if (articles.length === 0) {
      return (
        <Box
          sx={{
            width: '100%',
            height: { xs: '300px', sm: '350px', md: '350px', lg: '600px' },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            border: '1px solid #D5DCE1',
            borderRadius: '4px',
            padding: '40px 0',
          }}
        >
          <NoDataFound containerStyle={{ height: 'auto' }} isForKb={true} title="Data not found" showBackButton={false} />
        </Box>
      );
    }

    if (filteredArticles.length === 0 && searchQuery.trim()) {
      return (
        <Box
          sx={{
            width: '100%',
            height: { xs: '300px', sm: '350px', md: '350px', lg: '600px' },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            border: '1px solid #D5DCE1',
            borderRadius: '4px',
            padding: '40px 0',
          }}
        >
          <NoDataFound containerStyle={{ height: 'auto' }} isForKb={true} title="Data not found" showBackButton={false} />
        </Box>
      );
    }

    return filteredArticles?.map((article, index) => (
      <Box
        key={index}
        onClick={() => {
          if (article.slug && onArticleClick) {
            onArticleClick(article.id || article.slug);
          }
        }}
        sx={{
          mb: '10px',
          maxWidth: '840px',
          minHeight: '80px',
          cursor: article.slug ? 'pointer' : 'default',
          paddingLeft: '46px',
          py: '29px',
          pr: '29px',
          transition: 'all 0.2s',
          border: '1px solid #D5DCE1',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          },
          WebkitTapHighlightColor: 'transparent',

          '&:hover': {
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          },

          '&:focus': {
            outline: 'none',
            backgroundColor: '#FFFFFF',
          },

          '&:focus-visible': {
            outline: 'none',
            backgroundColor: '#FFFFFF',
          },

          '&:active': {
            backgroundColor: '#FFFFFF',
          },
        }}
      >
        <Typography
          sx={{
            color: '#0C141B',
            fontSize: '18px',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: 'normal',
          }}
        >
          {article.title}
        </Typography>
      </Box>
    ));
  };

  return (
    <Box
      data-kb-font-id={scopeId}
      sx={{
        display: 'flex',
        minHeight: { xs: '300px', sm: '350px', md: '350px', lg: minHeight },
        width: '100%',
        marginTop: { xs: '22px', sm: '32px', lg: hasCategories ? '128px' : '104px' },
        borderRadius: '4px',
        overflow: 'hidden',
      }}
    >
      {/* LEFT SIDEBAR */}
      {hasCategories && (
        <Box sx={{ width: '480px', bgcolor: 'white' }}>
          <Box sx={{ mx: '40px', marginBottom: '30px' }}>
            <SearchInput
              title=""
              placeholder="Search"
              className="SearchInputField-kb"
              value={searchQuery}
              onKeyDown={onKeyDown}
              onChange={handleSearchChange}
            />
          </Box>

          <Typography
            sx={{
              color: '#405261',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              mx: '40px',
              mb: '20px',
              lineHeight: '34px',
            }}
          >
            Categories
          </Typography>

          <List sx={{ p: 0 }}>
            {subCategories?.map((cat) => (
              <ListItem key={cat.slug || cat.name} disablePadding sx={{ mb: '15px' }}>
                <ListItemButton
                  selected={selectedCategory === cat.name}
                  onClick={() => {
                    setSelectedCategory(cat.name);
                    if (onSubCategoryClick && cat.slug) {
                      onSubCategoryClick(cat.slug);
                    }
                  }}
                  sx={{
                    borderRadius: '4px',
                    mx: '40px',
                    p: 0,
                    backgroundColor: '#FFFFFF!important',
                    '&.Mui-selected': {
                      marginLeft: 0,
                      marginRight: '40px',
                      backgroundColor: '#FFFFFF!important',
                    },
                    '&:hover': {
                      backgroundColor: '#FFFFFF !important',
                    },
                  }}
                >
                  {selectedCategory === cat.name && (
                    <EastIcon sx={{ color: '#148DFD', mr: '16px', fontSize: '24px' }} />
                  )}
                  <ListItemText
                    primary={cat.name}
                    sx={{ my: 0 }}
                    primaryTypographyProps={{
                      fontSize: '18px',
                      fontStyle: 'normal',
                      textTransform: 'capitalize',
                      fontWeight: selectedCategory === cat.name ? 700 : 400,
                      lineHeight: '34px',
                      color: selectedCategory === cat.name ? '#0C141B' : '#405261',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* RIGHT SIDE - Articles */}
      <Box
        sx={{
          flex: hasCategories ? 1 : 'none',
          width: hasCategories ? 'auto' : '100%',
          backgroundColor: 'white',
          maxWidth: '840px',
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          minHeight: '100%',
        }}
      >
        {!hasCategories && (
          <Box sx={{ width: '100%', mb: '30px', maxWidth: '100%' }}>
            <SearchInput
              title=""
              placeholder="Search"
              className="SearchInputField-kb"
              value={searchQuery}
              onKeyDown={onKeyDown}
              onChange={handleSearchChange}
            />
          </Box>
        )}

        <Box sx={{ width: '100%', marginTop: '5px' }}>
          {renderArticlesContent()}
        </Box>
      </Box>
    </Box>
  );
};

export default ArticleListKnowledgeBase;